/**
 * +field: the Mossvale zone (levels 1–10): maps, the intro chain, board bounties and the Hollow
 * Barrow's clear rewards.
 *
 * XP sizing (progression's curve, XP_CURVE_SCALE 6.4: 1→10 ≈ 13.6k XP; an even-level kill pays
 * 10+4L): the main quest pays ≈ 1.7k, one pass of the nine area bounties ≈ 5.5k (plus ≈ 5k from
 * their 126 kills), area discoveries ≈ 1.8k, the Barrow's first clear 1k. Following the journey
 * (quest + Barrow + discoveries, grinding the rest) or doing every bounty both land at 10 in about
 * 30–40 minutes of active play (docs/design/zones.md "Mossvale v2": Balance).
 */
import type { DungeonDef, DungeonId } from '@keystone/sim';
import { MOSSVALE_FIELD_MAPS } from './fields';
import { FIELD_NPC, MAP, MOB, PORTAL, TOWN_NPC, ZONE, ZONE_NPC } from './ids';
import { ZONE_SPOTS } from './mossvaleZone';
import { QUEST_NPC } from './mossvaleQuestNpcs';
import type { BountyDef, BountyStep, ZoneDef } from './types';

/** Waypoint helper: a spot on the zone map for the tracker arrow. */
const at = (x: number, y: number): BountyStep['waypoint'] => ({ mapId: MAP.mossvale, pos: { x, y } });
const atSpot = (p: { x: number; y: number }): BountyStep['waypoint'] => at(p.x, p.y);
const waystonePos = (id: string): BountyStep['waypoint'] => {
  const w = zone()?.field?.waystones?.find((x) => x.id === id);
  return w ? at(w.pos.x, w.pos.y) : undefined;
};
const zone = (): DungeonDef | undefined => MOSSVALE_FIELD_MAPS.find((d) => d.id === MAP.mossvale);
const npcPos = (npcId: string): BountyStep['waypoint'] => {
  const n = zone()?.field?.npcs?.find((x) => x.id === npcId);
  return n ? { mapId: MAP.mossvale, pos: { x: n.pos.x, y: n.pos.y } } : undefined;
};
const portalPos = (portalId: string): BountyStep['waypoint'] => {
  const p = zone()?.field?.portals.find((x) => x.id === portalId);
  return p ? { mapId: MAP.mossvale, pos: { x: p.pos.x, y: p.pos.y } } : undefined;
};
const M = MAP.mossvale;

/**
 * Mossvale's directed main quest (docs/design/zones.md "Mossvale's main quest"), routed through the
 * journey: Elder → the Slime Hollows → the Boar Run → the Hollow Barrow → the Auto tutorial → back
 * to town for the Hearthkeeper (idle play) and the board → the Lakeside Outpost → Greyhorn Heights →
 * Bramblewood → level 10 (the Pale Rise) → the Tower Road. Every step has
 * a waypoint except the open-ended ones.
 */
const MOSSVALE_CHAIN: BountyStep[] = [
  {
    id: 'mv_main_0_pell',
    kind: 'talk',
    title: 'Tea with Granny Pell',
    text: 'You wake on the bench by Granny Pell’s cottage. She has been waiting for you to stir.',
    target: QUEST_NPC.pell,
    count: 1,
    mapId: M,
    areaId: 'slime_hollows',
    waypoint: npcPos(QUEST_NPC.pell),
    reward: { xp: 40, gold: 5 },
  },
  {
    id: 'mv_main_2_slimes',
    kind: 'kill',
    title: 'Slime Season',
    text: 'The slimes have been at Granny Pell’s garden again. Pop six Meadow Slimes in the Slime Hollows, below her cottage.',
    target: MOB.meadowSlime,
    count: 6,
    mapId: M,
    areaId: 'slime_hollows',
    waypoint: atSpot(ZONE_SPOTS.slime),
    reward: { xp: 120, gold: 20 },
  },
  {
    id: 'mv_main_1_elder',
    kind: 'talk',
    title: 'The Road to Mossvale',
    text: 'Granny Pell says Elder Rowan will know what to do with you. Follow the road east out of the Hollows, past the Wayside Shrine, to Mossvale.',
    target: TOWN_NPC.elderRowan,
    count: 1,
    mapId: M,
    areaId: 'mossvale',
    waypoint: npcPos(TOWN_NPC.elderRowan),
    reward: { xp: 80, gold: 10 },
  },
  {
    id: 'mv_main_3_boars',
    kind: 'kill',
    title: 'Tusks in the Barley',
    text: 'Tusk Boars have been charging the farmhands at Brookside Farm. Take the west road back past the Hollows, then the farm track north to the Boar Run, and drive off four of them.',
    target: MOB.tuskBoar,
    count: 4,
    mapId: M,
    areaId: 'boar_run',
    waypoint: atSpot(ZONE_SPOTS.boarRun),
    reward: { xp: 220, gold: 30 },
  },
  {
    id: 'mv_main_4_ruin',
    kind: 'enter',
    title: 'Investigate the Glowing Ruin',
    text: 'Tobin saw a cold blue light in Barrowfield, east along the north road from the farm. See what is down there.',
    target: MAP.hollowBarrow,
    count: 1,
    mapId: M,
    areaId: 'hollow_barrow',
    waypoint: portalPos(PORTAL.barrowEntrance),
    reward: { xp: 100, gold: 15 },
  },
  {
    id: 'mv_main_5_barrow',
    kind: 'dungeon',
    title: 'Something Stirs Beneath the Meadow…',
    text: 'Something below is calling the dead. Clear the Hollow Barrow with your party.',
    target: MAP.hollowBarrow,
    count: 1,
    mapId: M,
    areaId: 'hollow_barrow',
    waypoint: portalPos(PORTAL.barrowEntrance),
    reward: { xp: 300, gold: 50 },
  },
  {
    id: 'mv_main_6_auto',
    kind: 'auto',
    title: 'Let Auto Take a Swing',
    text: 'Feeling tired of swinging? Press Z — your hero will fight on their own. Sit back and watch Auto defeat 10 enemies.',
    target: '',
    count: 10,
    mapId: M,
    reward: { xp: 150, gold: 20 },
  },
  {
    id: 'mv_main_7_hearth',
    kind: 'talk',
    title: 'Words by the Hearth',
    text: 'Hearthkeeper Bryn in Mossvale knows how a hero keeps going while you rest. Take the north road home and listen.',
    target: TOWN_NPC.hearthkeeper,
    count: 1,
    mapId: M,
    areaId: 'mossvale',
    waypoint: npcPos(TOWN_NPC.hearthkeeper),
    panel: 'idle_intro',
    reward: { xp: 120, gold: 20 },
  },
  {
    id: 'mv_main_8_board',
    kind: 'talk',
    title: 'Bounties by the Well',
    text: 'Folk around Mossvale post bounties on the board by the well. Now that you can hold your own, take a look.',
    target: TOWN_NPC.bountyBoard,
    count: 1,
    mapId: M,
    areaId: 'mossvale',
    waypoint: npcPos(TOWN_NPC.bountyBoard),
    reward: { xp: 80, gold: 15 },
  },
  {
    id: 'mv_main_8_lake',
    kind: 'goto',
    title: 'The Lake Road',
    text: 'Fisher Dunn swears something big is snapping at his lines on Glimmermere. Take the east road to the Lakeside Outpost.',
    target: M,
    count: 1,
    mapId: M,
    areaId: 'lakeside_outpost',
    waypoint: npcPos(ZONE_NPC.fisherDunn),
    reward: { xp: 160, gold: 25 },
  },
  {
    id: 'mv_main_9_heights',
    kind: 'goto',
    title: "Up Goat's Climb",
    text: "Keeper Maren hasn't heard from the miners at Tallowdeep. Follow the valley road north from the outpost and climb to Greyhorn Heights.",
    target: M,
    count: 1,
    mapId: M,
    areaId: 'greyhorn',
    waypoint: waystonePos('ws_climb'),
    reward: { xp: 200, gold: 25 },
  },
  {
    id: 'mv_main_9_bramblewood',
    kind: 'goto',
    title: 'Into Bramblewood',
    text: 'The Elder wants eyes on the bandits in Bramblewood. From the Barrowfield crossroads, take the west road into the trees.',
    target: M,
    count: 1,
    mapId: M,
    areaId: 'bramblewood',
    waypoint: atSpot(ZONE_SPOTS.brambleEdge),
    reward: { xp: 180, gold: 25 },
  },
  {
    id: 'mv_main_10_level',
    kind: 'level',
    title: 'Grow Strong',
    text: 'The woods, then the ruins on the Pale Rise: grow strong enough to leave Mossvale. Reach level 10.',
    target: '',
    count: 1,
    level: 10,
    mapId: M,
    reward: { xp: 0, gold: 50 },
  },
  {
    id: 'mv_main_11_tower',
    kind: 'talk',
    title: 'The Advancement Trial',
    text: 'Warden Ilsa keeps the Tower Road, over the Pale Rise at the north edge of Mossvale. Take her Advancement Trial and choose your specialisation.',
    target: FIELD_NPC.towerWarden,
    count: 1,
    mapId: M,
    areaId: 'tower_road',
    waypoint: npcPos(FIELD_NPC.towerWarden),
    panel: 'advancement',
    reward: { xp: 0, gold: 100 },
  },
];

const bounty = (b: Omit<BountyDef, 'kind' | 'mapId'>): BountyDef => ({ kind: 'kill', mapId: M, ...b });

/** +zone: bounties posted at the Lakeside Outpost (taken and turned in there). */
const outpost = { boardNpcId: ZONE_NPC.outpostBoard, boardAreaId: 'lakeside_outpost' } as const;

/**
 * Repeatable board bounties, one per area along the journey: the town board posts the near country,
 * the woods and the Rise; the Lakeside Outpost's board posts the lake and the heights (its turn-ins
 * are the one return trip to the outpost).
 */
const MOSSVALE_BOUNTIES: BountyDef[] = [
  bounty({ id: 'mv_bounty_slimes', title: 'Slime Sweep', text: 'Clear 15 slimes (Meadow or Bloom) out of the Slime Hollows.', target: MOB.meadowSlime, npcIds: [MOB.meadowSlime, MOB.bloomSlime], count: 15, areaId: 'slime_hollows', minLevel: 1, reward: { xp: 200, gold: 30 } }),
  bounty({ id: 'mv_bounty_boars', title: 'Boar Culling', text: 'The farmers will pay for 12 Tusk Boars from the Boar Run.', target: MOB.tuskBoar, npcIds: [MOB.tuskBoar, MOB.oldMossback], count: 12, areaId: 'boar_run', minLevel: 2, reward: { xp: 340, gold: 40 } }),
  bounty({ id: 'mv_bounty_barrowfield', title: 'Barrowfield Pests', text: 'Something under the Barrow has the beasts of Barrowfield riled. Thin out 12 of them.', target: MOB.tuskBoar, npcIds: [MOB.tuskBoar, MOB.bloomSlime], count: 12, areaId: 'barrowfield', minLevel: 3, reward: { xp: 420, gold: 45 } }),
  bounty({ id: 'mv_bounty_shore', title: 'Shore Patrol', text: 'Fisher Dunn wants his shore back: 15 Snappers or Croakers round Glimmermere.', target: MOB.reedbackSnapper, npcIds: [MOB.reedbackSnapper, MOB.mireCroaker, MOB.oldSnapjaw], count: 15, areaId: 'glimmermere', minLevel: 4, reward: { xp: 520, gold: 55 }, ...outpost }),
  bounty({ id: 'mv_bounty_hounds', title: 'Hound Hunt', text: 'Crag Hounds harry the valley road. Keeper Maren pays for 12.', target: MOB.cragHound, npcIds: [MOB.cragHound], count: 12, areaId: 'greyhorn', minLevel: 5, reward: { xp: 560, gold: 60 }, ...outpost }),
  bounty({ id: 'mv_bounty_tallow', title: 'Snuff the Candles', text: 'The Tallow folk have taken Tallowdeep Mine. Rout 15 of them.', target: MOB.tallowDigger, npcIds: [MOB.tallowDigger, MOB.tallowChanter, MOB.foremanTallowick], count: 15, areaId: 'greyhorn', minLevel: 6, reward: { xp: 700, gold: 70 }, ...outpost }),
  bounty({ id: 'mv_bounty_wolves', title: 'Wolf Pelts', text: 'Hilda needs pelts: hunt 15 wolves at the edge of Bramblewood.', target: MOB.brambleWolf, npcIds: [MOB.brambleWolf, MOB.greymaw], count: 15, areaId: 'bramblewood', minLevel: 7, reward: { xp: 760, gold: 70 } }),
  bounty({ id: 'mv_bounty_bandits', title: 'Bandit Bounty', text: 'Break the Briar gang: defeat 15 bandits (Briar Bandits, Slingers or their Captain).', target: MOB.briarBandit, npcIds: [MOB.briarBandit, MOB.banditSlinger, MOB.banditCaptain], count: 15, areaId: 'bramblewood', minLevel: 8, reward: { xp: 980, gold: 100 } }),
  bounty({ id: 'mv_bounty_rise', title: 'Quiet the Rise', text: 'Lay 15 Pale Sentinels or Hollow Shades to rest on the Pale Rise.', target: MOB.paleSentinel, npcIds: [MOB.paleSentinel, MOB.hollowShade, MOB.paleWarden], count: 15, areaId: 'pale_rise', minLevel: 9, reward: { xp: 1050, gold: 90 } }),
];

export const MOSSVALE: ZoneDef = {
  id: ZONE.mossvale,
  name: 'Mossvale',
  levelRange: [1, 10],
  townId: MAP.mossvale,
  townAreaId: 'mossvale',
  boardStepId: 'mv_main_8_board',
  dungeonBoardAfterStepId: 'mv_main_5_barrow',
  mapIds: [MAP.mossvale],
  dungeonIds: [MAP.hollowBarrow],
  chain: MOSSVALE_CHAIN,
  bounties: MOSSVALE_BOUNTIES,
  dungeonRewards: [
    // ≈ one level at L4 (xpToNext(4) = 1000); repeat clears ≈ 25%.
    { dungeonId: MAP.hollowBarrow, firstClear: { xp: 1000, gold: 200 }, repeatClear: { xp: 250, gold: 60 }, minLevel: 3 },
  ],
};

/** Every zone, by id. */
export const ZONES: Record<string, ZoneDef> = { [ZONE.mossvale]: MOSSVALE };

/** The zone a map (town, field or zone dungeon) belongs to, or null (M+ dungeons, tests). */
export function zoneOfMap(mapId: DungeonId): ZoneDef | null {
  for (const z of Object.values(ZONES)) if (z.mapIds.includes(mapId) || z.dungeonIds.includes(mapId)) return z;
  return null;
}

/** Every open map (town + fields) across all zones (DungeonDefs with `field`). */
export function fieldMaps(): DungeonDef[] {
  return MOSSVALE_FIELD_MAPS;
}

/** Mob ids a kill step / bounty accepts. */
export function bountyNpcIds(step: BountyStep): string[] {
  return step.kind === 'kill' ? (step.npcIds ?? [step.target]) : [];
}

/** A board bounty by id (any zone). */
export function bountyById(id: string): BountyDef | null {
  for (const z of Object.values(ZONES)) for (const b of z.bounties) if (b.id === id) return b;
  return null;
}
